import { SelectionModel } from '@angular/cdk/collections';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { HardwareDevice, HardwareDeviceSearch, HardwareService } from '../services/hw.service';
import { isValidMacAddressForDeviceType, macAddressFormatHint } from '../services/mac-address';
import { BulkAddHardwareToSite, SiteService, SyncConfigWearable, TagBatteryStatus } from './../services/site.service';

@Component({
  selector: 'app-sitedetails-tags',
  templateUrl: './sitedetails-tags.component.html',
  styleUrls: ['./sitedetails-tags.component.css'],
  standalone: false,
})
export class SitedetailsTagsComponent implements OnInit {
  constructor(
    public dialog: MatDialog,
    private siteService: SiteService,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar
  ) {}
  @ViewChild('tagTable', { static: true }) tagTable: MatTable<SyncConfigWearable>;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumnsTags = [
    'select',
    'id',
    'bleMacAddress',
    'createdAt',
    'firstTurnOnUtcDateTime',
    'assignedToPerson',
    'missingSince',
    'isSubscription',
    'productionTime',
    'batteryStatus',
    'options',
  ];

  public selected = new SelectionModel<SyncConfigWearable>(true, []);
  private siteId: string;
  private siteName: string = '';
  public tags: SyncConfigWearable[] = [];
  public dataSource = new MatTableDataSource<SyncConfigWearable>([]);
  public sortActive: string = 'id';
  public sortDirection: 'asc' | 'desc' = 'desc';

  public filterAssignedToPerson: 'all' | 'assigned' | 'unassigned' = 'all';
  public filterBatteryStatus: TagBatteryStatus[] = [];
  public filterIsMissing: 'all' | 'missing' | 'notmissing' = 'all';
  public filterIsActivated: 'all' | 'activated' | 'notactivated' = 'all';
  public filterShowOnlySelected: boolean = false;

  public batteryStatusOptions: TagBatteryStatus[] = [
    TagBatteryStatus.Ok,
    TagBatteryStatus.Low,
    TagBatteryStatus.LowLow,
    TagBatteryStatus.NeverTurnedOn,
  ];

  ngOnInit(): void {
    this.siteId = this.route.snapshot.paramMap.get('siteId');
    this.siteService.getSite(this.siteId).subscribe((s) => (this.siteName = s.name || this.siteId));
    this.getTags();
    this.dataSource.filterPredicate = (tag: SyncConfigWearable, filter: string) => {
      const filterObj = JSON.parse(filter);

      // Filter on assigned to person
      if (filterObj.filterAssignedToPerson === 'assigned' && !this.isAssignedToPerson(tag)) return false;
      if (filterObj.filterAssignedToPerson === 'unassigned' && this.isAssignedToPerson(tag)) return false;

      // Filter on battery status
      if (filterObj.filterBatteryStatus?.length > 0) {
        const status = this.getBatteryStatus(tag);
        if (!filterObj.filterBatteryStatus.includes(status)) return false;
      }

      // Missing filter
      if (filterObj.filterIsMissing === 'missing' && !this.isMissing(tag)) return false;
      if (filterObj.filterIsMissing === 'notmissing' && this.isMissing(tag)) return false;

      // Activation filter
      if (filterObj.filterIsActivated === 'activated' && !this.isActivated(tag)) return false;
      if (filterObj.filterIsActivated === 'notactivated' && this.isActivated(tag)) return false;

      // Show only selected filter
      if (filterObj.filterShowOnlySelected && !this.selected.isSelected(tag)) return false;

      return true;
    };
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.sort.active = this.sortActive;
    this.sort.direction = this.sortDirection;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'id':
          return item.hardwareDevice?.id || '';
        case 'bleMacAddress':
          return item.hardwareDevice?.bleMacAddress || '';
        case 'createdAt':
          return item.hardwareDevice?.createdAt || '';
        case 'firstTurnOnUtcDateTime':
          return item.firstTurnOnUtcDateTime || '';
        case 'assignedToPerson':
          return item.person ? 1 : 0;
        case 'missingSince':
          return this.getMissingSince(item) || '';
        case 'isSubscription':
          return item.isBoughtUnderSubscription ? 1 : 0;
        case 'productionTime':
          return item.hardwareDevice?.productionDate || '';
        case 'batteryStatus':
          const status = this.getBatteryStatus(item);
          switch (status) {
            case TagBatteryStatus.Ok:
              return 2;
            case TagBatteryStatus.Low:
              return 1;
            case TagBatteryStatus.LowLow:
              return 0;
            case TagBatteryStatus.NeverTurnedOn:
              return -1;
            default:
              return -2;
          }
        default:
          return item[property] ?? '';
      }
    };
  }

  onSortChange(event) {
    this.sortActive = event.active;
    this.sortDirection = event.direction;
    this.dataSource.sort = this.sort;
  }

  getTags() {
    this.siteService.getTags(this.siteId).subscribe((s) => {
      this.tags = s;
      this.dataSource.data = s;
    });
  }
  onDeleteSelectedClick() {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent);
    confirmDialogRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        this.disconnectTags(this.selected.selected.map((t) => t.id));
        this.selected.clear();
      }
    });
  }
  isAllSelected() {
    const numSelected = this.selected.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }
  masterToggle() {
    if (this.isAllSelected()) {
      this.selected.clear();
    } else {
      this.dataSource.data.forEach((tag) => this.selected.select(tag));
    }
  }

  onNewTagClick() {
    const connectTagDialogRef = this.dialog.open(ConnectTagComponent, {
      width: '80%',
    });
    connectTagDialogRef.afterClosed().subscribe((tagIds) => {
      if (tagIds && tagIds.length > 0) {
        this.siteService
          .connectHardwareDevices(this.siteId, {
            safeMode: false,
            hardwareIds: tagIds,
          })
          .subscribe(
            (s) => {
              this.snackBar.open(s, null, { duration: 4000 });
              this.getTags();
            },
            (err: HttpErrorResponse) => {
              this.snackBar.open('There was an error connecting tags ' + err.error, null, { duration: 10000 });
              console.error(err);
            }
          );
      }
    });
  }
  deleteTag(tag: SyncConfigWearable) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent);
    confirmDialogRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        this.disconnectTags([tag.id]);
      }
    });
  }

  disconnectTags(tagIds: string[]) {
    const bulkRemoveHardwareToSite: BulkAddHardwareToSite = {
      safeMode: false,
      hardwareIds: tagIds,
    };
    this.siteService.disconnectHardwareDevices(this.siteId, bulkRemoveHardwareToSite).subscribe(
      (s) => {
        this.snackBar.open(s, null, { duration: 4000 });
        this.getTags();

        this.selected.clear();
      },
      (err: HttpErrorResponse) => {
        this.snackBar.open('There was an error disconnecting tags ' + err.error, null, { duration: 10000 });
        console.error(err);
      }
    );
  }

  onConnectTagsByMac() {
    const connectTagsByMacDialogRef = this.dialog.open(InputListOfMacsComponent, {
      data: {
        title: 'Connect tags by list of mac addresses.',
        deviceType: 'SRT-PT',
      },
      width: '80%',
    });
    connectTagsByMacDialogRef.afterClosed().subscribe((data) => {
      if (data.macAddresses && data.macAddresses.length > 0) {
        this.siteService
          .connectHardwareDevices(
            this.siteId,
            {
              safeMode: false,
              hardwareMacs: data.macAddresses,
              hardwareType: data.deviceType,
              productionDate: data.productionDate,
              isBoughtUnderSubscription: data.isBoughtUnderSubscription,
            },
            data.addMacIfNotExists,
            data.moveHardwareIfAlreadyConnected
          )
          .subscribe(
            (s) => {
              this.snackBar.open(s, null, { duration: 4000 });
              this.getTags();
            },
            (err: HttpErrorResponse) => {
              this.snackBar.open('There was an error connecting tags ' + err.error, null, { duration: 10000 });
              console.error(err);
            }
          );
      }
    });
  }

  onSelectTagsByMac() {
    const selectTagsByMacDialogRef = this.dialog.open(InputListOfMacsComponent, {
      data: {
        title: 'Select tags in list based on list of mac addresses (full MAC or last 4 symbols XX:XX).',
      },
      width: '80%',
    });
    selectTagsByMacDialogRef.afterClosed().subscribe((data) => {
      if (data.macAddresses && data.macAddresses.length > 0) {
        const tagsToSelect: SyncConfigWearable[] = [];
        let hasMultipleMatches = false;
        let duplicateShortMac = '';

        // Maybe could be rewriten in more performant way in future
        data.macAddresses.forEach((inputMac: string) => {
          const formattedMac = inputMac.trim().toLowerCase();
          const matchingTags: SyncConfigWearable[] = [];

          this.tags.forEach((tag) => {
            const tagMacAddress = tag.hardwareDevice.bleMacAddress?.toLowerCase();
            if (tagMacAddress) {
              if (tagMacAddress === formattedMac) {
                // full MAC address match
                matchingTags.push(tag);
              } else if (formattedMac.length === 5 && formattedMac.includes(':')) {
                // last 4 symbols match (AB:CD)
                const tagLastFiveSymbols = tagMacAddress.slice(-5);
                if (tagLastFiveSymbols === formattedMac) {
                  matchingTags.push(tag);
                }
              }
            }
          });

          // Check for multiple matches when using short format
          if (formattedMac.length === 5 && formattedMac.includes(':') && matchingTags.length > 1) {
            hasMultipleMatches = true;
            duplicateShortMac = formattedMac;
          } else {
            tagsToSelect.push(...matchingTags);
          }
        });

        if (hasMultipleMatches) {
          // duplicate, using last 4 symbols is not enough to identify unique tags
          this.snackBar.open(
            `Multiple tags found with the same last 4 symbols: ${duplicateShortMac}. Please use full MAC address.`,
            null,
            { duration: 6000 }
          );
          return;
        } else {
          // Select all matching tags
          tagsToSelect.forEach((tag) => {
            this.selected.select(tag);
          });
        }
      }
    });
  }

  formatDate(dateString: string | Date): string {
    return dateString ? new Date(dateString).toISOString().split('T')[0] : 'unknown';
  }

  isAssignedToPerson(tag: SyncConfigWearable): boolean {
    return tag.person != null && tag.person !== undefined;
  }

  isMissing(tag: SyncConfigWearable): boolean {
    if (tag.issues?.find((p) => p.type === 100)) {
      return true;
    }

    return false;
  }

  isActivated(tag: SyncConfigWearable): boolean {
    return tag.firstTurnOnUtcDateTime != null && tag.firstTurnOnUtcDateTime !== undefined;
  }

  getMissingSince(tag: SyncConfigWearable): string | null {
    const missingIssue = tag.issues?.find((i) => i.type === 100);
    return missingIssue?.activatedTime ?? null;
  }

  getBatteryStatus(tag: SyncConfigWearable): TagBatteryStatus {
    if (!tag.firstTurnOnUtcDateTime) {
      return TagBatteryStatus.NeverTurnedOn;
    }

    if (tag.issues.find((i) => i.type === 101)) {
      return TagBatteryStatus.Low;
    } else if (tag.issues.find((i) => i.type === 102)) {
      return TagBatteryStatus.LowLow;
    }

    return TagBatteryStatus.Ok;
  }

  applyFilters() {
    this.dataSource.filter = JSON.stringify({
      filterAssignedToPerson: this.filterAssignedToPerson,
      filterBatteryStatus: this.filterBatteryStatus,
      filterIsMissing: this.filterIsMissing,
      filterIsActivated: this.filterIsActivated,
      filterShowOnlySelected: this.filterShowOnlySelected,
    });
  }

  clearFilters() {
    this.filterAssignedToPerson = 'all';
    this.filterBatteryStatus = [];
    this.filterIsMissing = 'all';
    this.filterIsActivated = 'all';
    this.filterShowOnlySelected = false;
    this.applyFilters();
  }

  selectAllFiltered() {
    this.dataSource.filteredData.forEach((tag) => this.selected.select(tag));
  }

  copySelectedMacsToClipboard(): void {
    const macAddresses = this.selected.selected
      .map((tag) => tag.hardwareDevice?.bleMacAddress?.toLowerCase())
      .filter((mac) => mac)
      .join('\n');

    if (!macAddresses) {
      this.snackBar.open('No valid MAC addresses found in the selected tags', null, { duration: 3000 });
      return;
    }

    navigator.clipboard
      .writeText(macAddresses)
      .then(() => {
        this.snackBar.open(`${this.selected.selected.length} MAC addresses copied to clipboard`, null, {
          duration: 3000,
        });
      })
      .catch((err) => {
        console.error('Failed to copy MAC addresses: ', err);
        this.snackBar.open('Failed to copy MAC addresses to clipboard', null, { duration: 3000 });
      });
  }

  exportSelectedToCsv(): void {
    const headers = [
      'Id',
      'BLE MAC Address',
      'Created At',
      'First Turn On',
      'Assigned To Person',
      'Missing Since',
      'Is Subscription',
      'Production Date',
      'Battery Status',
    ];

    const escape = (val: any): string => {
      const str = val == null ? '' : String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const rows = this.selected.selected.map((tag) => [
      escape(tag.hardwareDevice?.id),
      escape(tag.hardwareDevice?.bleMacAddress),
      escape(this.formatDate(tag.hardwareDevice?.createdAt)),
      escape(this.formatDate(tag.firstTurnOnUtcDateTime)),
      escape(tag.person ? tag.person.fullName : 'No'),
      escape(this.getMissingSince(tag) ? this.formatDate(this.getMissingSince(tag)) : 'Not missing'),
      escape(tag.isBoughtUnderSubscription ? 'Yes' : 'No'),
      escape(tag.hardwareDevice?.productionDate ? this.formatDate(tag.hardwareDevice.productionDate) : ''),
      escape(this.getBatteryStatus(tag)),
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tags-export-${this.siteName}-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
}

@Component({
  selector: 'app-connect-tag-dialog',
  template: `<mat-card appearance="outlined">
    <h3>Connect tags</h3>
    <app-hardware-search-field (onSubmit)="queryHardwareFormSubmit($event)"></app-hardware-search-field>
    <app-hardware-table
      [(orderBy)]="query.OrderBy"
      [(orderDirection)]="query.OrderDirection"
      [(limit)]="query.Limit"
      [(page)]="query.Page"
      [(selectedHardware)]="selectedHardware"
      [hardwareDevices]="hardwareDevices"
      [displayedColumns]="displayedColumns"
      (change)="getHardwareDevices()"
    ></app-hardware-table>
    <mat-card-actions>
      <button mat-raised-button (click)="onSaveClick()">Connect all {{ selectedHardware.length }} matching tags</button>
      <button mat-button (click)="onCancelClick()">Cancel</button>
    </mat-card-actions>
  </mat-card> `,
  standalone: false,
})
export class ConnectTagComponent {
  constructor(public dialogRef: MatDialogRef<ConnectTagComponent>, private hwService: HardwareService) {}
  public hardwareDevices: HardwareDevice[] = [];
  public hardwareDeviceSearch: HardwareDeviceSearch;
  public selectedHardware: HardwareDevice[] = [];
  displayedColumns = ['select', 'id', 'serial', 'macAddress', 'bleMacAddress', 'deviceType'];
  standardQuery: HardwareDeviceSearch = {
    Serial: '',
    MacAddress: '',
    DeviceType: 'SRT-PT',
    Limit: 100,
    Page: 0,
    OrderBy: 'Id',
    OrderDirection: 'asc',
  };
  query = Object.assign(this.standardQuery);
  onSaveClick(): void {
    const tagIds = this.selectedHardware.map((hd) => hd.id);
    this.dialogRef.close(tagIds);
  }
  onCancelClick(): void {
    this.dialogRef.close();
  }
  queryHardwareFormSubmit(search: HardwareDeviceSearch) {
    this.query = {
      ...this.standardQuery,
      Limit: this.query.Limit,
      OrderBy: this.query.OrderBy,
      OrderDirection: this.query.OrderDirection,
      ...search,
    };
    this.getHardwareDevices();
  }
  getHardwareDevices(): void {
    this.query.DeviceType = 'SRT-PT';
    this.hwService.searchHardware(this.query).subscribe((data) => {
      this.hardwareDevices = data;
    });
  }
}

@Component({
  selector: 'app-insert-list-of-macs-dialog',
  template: `<mat-card appearance="outlined">
    <h3>{{ data.title }}</h3>
    <form (ngSubmit)="connectTagsByMac(form)" #form="ngForm">
      <div style="display:flex; align-items:flex-start; gap:16px; margin-left:20px;">
        <mat-form-field>
          <textarea
            ngModel
            required
            matInput
            cdkTextareaAutosize
            #autosize="cdkTextareaAutosize"
            cdkAutosizeMinRows="1"
            cdkAutosizeMaxRows="20"
            name="macAddresses"
            placeholder="MacAddresses one per line."
            (input)="onMacsInput($event)"
            (blur)="onMacsBlur($event)"
          ></textarea>
        </mat-form-field>
        <div style="display:flex; flex-direction:column; gap:8px; padding-top:4px;">
          <mat-form-field appearance="outline">
            <mat-label>Production Date</mat-label>
            <input matInput type="text" ngModel name="productionDate" placeholder="DD/MM/YYYY" maxlength="10" (input)="autoFormatDate($event)" />
          </mat-form-field>
          <mat-checkbox ngModel name="isBoughtUnderSubscription"> Bought under subscription </mat-checkbox>
        </div>
      </div>
      <div *ngIf="invalidMacs.length > 0" class="mac-format-error">
        <strong>{{ invalidMacs.length }} MAC address(es) have an invalid format:</strong>
        <div>Expected {{ macFormatHint }}.</div>
        <ul>
          <li *ngFor="let mac of invalidMacs">{{ mac }}</li>
        </ul>
      </div>
      <button mat-raised-button type="submit" [disabled]="invalidMacs.length > 0">Confirm</button>
      <div *ngIf="data.deviceType">
        <mat-checkbox [(ngModel)]="data.addMacIfNotExists" name="addMacIfNotExists">
          Register MAC addresses if they are not found in database
        </mat-checkbox>
        <br />
        <mat-checkbox
          *ngIf="data.deviceType == 'SRT-PT'"
          [(ngModel)]="data.moveHardwareIfAlreadyConnected"
          name="moveHardwareIfAlreadyConnected"
        >
          Move hardware to this site, even if it is already registered with another site
        </mat-checkbox>
      </div>
      <input hidden name="deviceType" [(ngModel)]="data.deviceType" />
    </form>
    <mat-card-actions>
      <button mat-button (click)="onCancelClick()">Cancel</button>
    </mat-card-actions>
  </mat-card> `,
  styles: [
    `
      .mac-format-error {
        color: #b00020;
        margin: 8px 0 16px 20px;
      }
      .mac-format-error ul {
        margin: 4px 0 0 0;
        font-family: monospace;
      }
    `,
  ],
  standalone: false,
})
export class InputListOfMacsComponent {
  constructor(
    public dialogRef: MatDialogRef<InputListOfMacsComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      deviceType?: string;
      addMacIfNotExists?: boolean;
      moveHardwareIfAlreadyConnected?: boolean;
    }
  ) {}

  public invalidMacs: string[] = [];
  public macFormatHint: string = '';

  /**
   * Validates as the operator types so a malformed MAC is visible before they reach Confirm,
   * rather than after. The line still being typed is skipped — flagging a half-entered MAC
   * would mark every address invalid while it is being written.
   */
  validateMacs(value: string, skipLastLine: boolean = true): void {
    if (!this.data.deviceType) return;

    this.macFormatHint = macAddressFormatHint(this.data.deviceType);
    const lines = (value ?? '').toLowerCase().split('\n');
    const lastIndex = lines.length - 1;

    this.invalidMacs = lines
      .map((mac, index) => ({ mac: mac.trim(), index }))
      .filter(({ mac, index }) => {
        if (mac.length === 0) return false;
        if (skipLastLine && index === lastIndex) return false;
        return !isValidMacAddressForDeviceType(mac, this.data.deviceType);
      })
      .map(({ mac }) => mac);
  }

  onMacsInput(event: Event): void {
    this.validateMacs((event.target as HTMLTextAreaElement).value);
  }

  /** On blur the operator is done typing, so the final line is checked too. */
  onMacsBlur(event: Event): void {
    this.validateMacs((event.target as HTMLTextAreaElement).value, false);
  }

  autoFormatDate(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value.replace(/\D/g, '');
    if (val.length >= 3 && val.length <= 4) val = val.slice(0, 2) + '/' + val.slice(2);
    else if (val.length > 4) val = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4, 8);
    input.value = val;
  }

  /**
   * Blocks submission when any line is not a well formed MAC for the chosen device type.
   * Only the connect/insert dialogs pass a deviceType; the "select in list" dialogs do not,
   * and those accept a short "xx:xx" suffix, so they are deliberately left unvalidated.
   */
  connectTagsByMac(form: NgForm): void {
    const macField: string = form.value.macAddresses;
    const deviceType: string | null = form.value.deviceType;
    const macAddresses: string[] = macField
      .toLowerCase()
      .split('\n')
      .map((mac) => mac.trim())
      .filter((mac) => mac.length > 0);

    if (deviceType) {
      this.validateMacs(macField, false);
      if (this.invalidMacs.length > 0) {
        return;
      }
    }

    const addMacIfNotExists: boolean = form.value.addMacIfNotExists ?? false;
    const moveHardwareIfAlreadyConnected: boolean = form.value.moveHardwareIfAlreadyConnected ?? false;
    const rawDate: string = form.value.productionDate || '';
    let productionDate: string | null = null;
    if (rawDate) {
      const parts = rawDate.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        productionDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    const isBoughtUnderSubscription: boolean = form.value.isBoughtUnderSubscription ?? false;
    this.dialogRef.close({
      macAddresses,
      deviceType,
      addMacIfNotExists,
      moveHardwareIfAlreadyConnected,
      productionDate,
      isBoughtUnderSubscription,
    });
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
